import { useTranslation } from 'react-i18next';
import {
  Shield,
  Lock,
  FileCheck,
  Clock,
  Eye,
  EyeOff,
  Users,
  History,
  CheckCircle2,
  Scale,
  Server,
  Key,
  Fingerprint,
  FileText,
  Archive,
  Bell,
  ExternalLink,
} from 'lucide-react';

interface ComplianceItemProps {
  icon: React.ReactNode;
  title: string;
  legalReference: string;
  requirement: string;
  implementation: string;
  status: 'compliant' | 'partial' | 'pending';
}

const ComplianceItem = ({
  icon,
  title,
  legalReference,
  requirement,
  implementation,
  status,
}: ComplianceItemProps) => {
  const { t } = useTranslation();

  const statusConfig = {
    compliant: {
      bg: 'bg-success-50',
      border: 'border-success-200',
      icon: <CheckCircle2 className="h-5 w-5 text-success-600" />,
      label: t('vault.compliance.status.compliant', 'Établi'),
    },
    partial: {
      bg: 'bg-amber-50',
      border: 'border-amber-200',
      icon: <CheckCircle2 className="h-5 w-5 text-amber-600" />,
      label: t('vault.compliance.status.partial', 'Partiel'),
    },
    pending: {
      bg: 'bg-neutral-50',
      border: 'border-neutral-200',
      icon: <Clock className="h-5 w-5 text-neutral-500" />,
      label: t('vault.compliance.status.pending', 'En cours'),
    },
  };

  const config = statusConfig[status];

  return (
    <div className={`rounded-xl border ${config.border} ${config.bg} p-5`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
            {icon}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-semibold text-neutral-900">{title}</h3>
              <span className="rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-700">
                {legalReference}
              </span>
            </div>
            <p className="mt-1 text-sm text-neutral-600">{requirement}</p>
            <div className="mt-3 rounded-lg bg-white/60 p-3">
              <p className="text-sm font-medium text-neutral-700">
                {t('vault.compliance.ourImplementation', 'Notre implémentation :')}
              </p>
              <p className="mt-1 text-sm text-neutral-600">{implementation}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-full bg-white px-3 py-1 shadow-sm">
          {config.icon}
          <span className="text-sm font-medium text-neutral-700">{config.label}</span>
        </div>
      </div>
    </div>
  );
};

interface SecurityFeatureProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  details: string[];
}

const SecurityFeature = ({ icon, title, description, details }: SecurityFeatureProps) => (
  <div className="rounded-xl border border-neutral-200 bg-white p-5">
    <div className="flex items-start gap-4">
      <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50">
        {icon}
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-neutral-900">{title}</h3>
        <p className="mt-1 text-sm text-neutral-600">{description}</p>
        <ul className="mt-3 space-y-1">
          {details.map((detail, index) => (
            <li key={index} className="flex items-center gap-2 text-sm text-neutral-600">
              <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-success-500" />
              {detail}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </div>
);

/**
 * VaultComplianceContent - Contenu de conformité réutilisable
 *
 * Utilisé par VaultCompliancePage (vault users) et VaultComplianceDashboardPage (gestionnaires)
 */
export function VaultComplianceContent() {
  const { t } = useTranslation();

  return (
    <>
      {/* Introduction */}
      <div className="mb-8 rounded-xl border border-primary-200 bg-gradient-to-br from-primary-50 to-white p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-primary-100">
            <Scale className="h-6 w-6 text-primary-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-neutral-900">
              {t('vault.compliance.intro.title', 'Cadre légal du coffre-fort numérique')}
            </h2>
            <p className="mt-2 text-neutral-600">
              {t(
                'vault.compliance.intro.description',
                "Cette page distingue les exigences légales, les fonctions observées dans le produit et les garanties qui restent à vérifier. Elle ne vaut ni certification ni attestation de conformité globale."
              )}
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                Loi El Khomri (2016)
              </span>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                Décret n° 2016-1762
              </span>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                RGPD (2018)
              </span>
              <span className="rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                Code du travail Art. L3243-2
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Cadre légal */}
      <section className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <FileText className="h-5 w-5 text-primary-600" />
          {t('vault.compliance.legalFramework.title', 'Cadre Légal')}
        </h2>
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <div className="prose prose-sm max-w-none text-neutral-600">
            <p>
              {t(
                'vault.compliance.legalFramework.p1',
                "La loi n° 2016-1088 du 8 août 2016 (dite \"Loi Travail\" ou \"Loi El Khomri\") a introduit la possibilité pour les employeurs de remettre les bulletins de paie sous forme électronique, sous réserve de garanties spécifiques."
              )}
            </p>
            <p className="mt-3">
              {t(
                'vault.compliance.legalFramework.p2',
                "Le décret n° 2016-1762 du 16 décembre 2016 précise les modalités de mise en œuvre, notamment l'obligation de garantir l'intégrité, la disponibilité et la confidentialité des bulletins de paie pendant une durée minimale de 50 ans ou jusqu'aux 75 ans du salarié."
              )}
            </p>
            <p className="mt-3">
              {t(
                'vault.compliance.legalFramework.p3',
                "Le Règlement Général sur la Protection des Données (RGPD) impose des exigences supplémentaires en matière de sécurité et de protection des données personnelles contenues dans ces documents."
              )}
            </p>
          </div>
        </div>
      </section>

      {/* Conformité point par point */}
      <section className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <CheckCircle2 className="h-5 w-5 text-primary-600" />
          {t('vault.compliance.requirements.title', 'Analyse point par point')}
        </h2>
        <div className="space-y-4">
          <ComplianceItem
            icon={<Clock className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.retention.title', 'Durée de conservation')}
            legalReference="Art. D3243-8"
            requirement={t(
              'vault.compliance.items.retention.requirement',
              "Les bulletins de paie doivent être conservés pendant 50 ans ou jusqu'à ce que le salarié atteigne l'âge de 75 ans."
            )}
            implementation={t(
              'vault.compliance.items.retention.implementation',
              "Le code calcule une échéance à partir de 50 ans après le document et de l'âge de 75 ans. Le choix de conservation de l'employeur, la base juridique d'une durée plus longue et le comportement réel doivent être confirmés."
            )}
            status="partial"
          />

          <ComplianceItem
            icon={<Eye className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.accessibility.title', 'Accessibilité permanente')}
            legalReference="Art. D3243-7"
            requirement={t(
              'vault.compliance.items.accessibility.requirement',
              "L'employeur doit garantir au salarié un accès permanent à ses bulletins de paie pendant toute la durée de conservation."
            )}
            implementation={t(
              'vault.compliance.items.accessibility.implementation',
              "Une interface permet au salarié de consulter et récupérer ses documents. La disponibilité, les sauvegardes et la continuité de service doivent être confirmées par les contrats et les preuves d'exploitation."
            )}
            status="pending"
          />

          <ComplianceItem
            icon={<Lock className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.integrity.title', 'Intégrité des documents')}
            legalReference="Art. D3243-7"
            requirement={t(
              'vault.compliance.items.integrity.requirement',
              "L'intégrité des bulletins de paie doit être garantie pendant toute la durée de conservation."
            )}
            implementation={t(
              'vault.compliance.items.integrity.implementation',
              "Le code calcule un hash SHA-256 et utilise AES-256-GCM pour les coffres concernés. L'horodatage observé n'est pas démontré comme qualifié eIDAS et ne peut pas être présenté comme une preuve à date certaine."
            )}
            status="partial"
          />

          <ComplianceItem
            icon={<EyeOff className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.confidentiality.title', 'Confidentialité')}
            legalReference="RGPD Art. 32"
            requirement={t(
              'vault.compliance.items.confidentiality.requirement',
              "Les données personnelles doivent être protégées contre les accès non autorisés par des mesures techniques et organisationnelles appropriées."
            )}
            implementation={t(
              'vault.compliance.items.confidentiality.implementation',
              "Les coffres salariés en mode zero-knowledge chiffrent les documents côté navigateur. D'autres coffres utilisent un chiffrement côté serveur : la portée exacte dépend du type de coffre."
            )}
            status="partial"
          />

          <ComplianceItem
            icon={<Users className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.separation.title', 'Séparation des rôles')}
            legalReference="Art. D3243-7"
            requirement={t(
              'vault.compliance.items.separation.requirement',
              "Le gestionnaire (employeur) ne doit pas pouvoir accéder au contenu des documents une fois déposés dans le coffre-fort du salarié."
            )}
            implementation={t(
              'vault.compliance.items.separation.implementation',
              "Le modèle distingue les rôles de dépôt et de lecture. Cette séparation doit encore être prouvée par des tests anti-fuite sur chaque type de coffre et chaque parcours de dépôt."
            )}
            status="partial"
          />

          <ComplianceItem
            icon={<History className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.traceability.title', 'Traçabilité')}
            legalReference="RGPD Art. 30"
            requirement={t(
              'vault.compliance.items.traceability.requirement',
              "Un registre des traitements et une piste d'audit doivent permettre de tracer toutes les opérations effectuées sur les données."
            )}
            implementation={t(
              'vault.compliance.items.traceability.implementation',
              "Les entrées d'audit sont chaînées par hash et une vérification de chaîne existe. Le caractère exhaustif des événements et la conservation de cette chaîne doivent encore être éprouvés en environnement réel."
            )}
            status="partial"
          />

          <ComplianceItem
            icon={<Bell className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.closureNotice.title', 'Préavis de fermeture')}
            legalReference="Art. D3243-8"
            requirement={t(
              'vault.compliance.items.closureNotice.requirement',
              "En cas de fermeture du service, l'employeur doit informer le salarié au moins 3 mois avant et lui permettre de récupérer ses documents."
            )}
            implementation={t(
              'vault.compliance.items.closureNotice.implementation',
              "Un état de fermeture existe dans le code. L'acheminement automatique du préavis, l'export intégral et le délai effectif de trois mois ne sont pas encore prouvés de bout en bout."
            )}
            status="pending"
          />

          <ComplianceItem
            icon={<FileCheck className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.timestamp.title', 'Horodatage électronique')}
            legalReference="eIDAS Art. 41"
            requirement={t(
              'vault.compliance.items.timestamp.requirement',
              "Un horodatage électronique qualifié bénéficie d'une présomption d'exactitude de la date et de l'heure qu'il indique."
            )}
            implementation={t(
              'vault.compliance.items.timestamp.implementation',
              "Le service actuel conserve un horodatage technique ou un secours local. Il ne construit pas encore une requête RFC 3161 complète et ne vérifie pas cryptographiquement la signature du jeton : la qualification eIDAS n'est pas établie."
            )}
            status="pending"
          />

          <ComplianceItem
            icon={<Fingerprint className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.items.authentication.title', 'Authentification forte')}
            legalReference="RGPD Art. 32"
            requirement={t(
              'vault.compliance.items.authentication.requirement',
              "L'accès aux données sensibles doit être protégé par des mécanismes d'authentification robustes."
            )}
            implementation={t(
              'vault.compliance.items.authentication.implementation',
              "Une garde 2FA existe pour les coffres zero-knowledge. Son application à tous les parcours sensibles et le blocage opérationnel des tentatives suspectes restent à vérifier en environnement réel."
            )}
            status="partial"
          />
        </div>
      </section>

      {/* Sécurité technique */}
      <section className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <Server className="h-5 w-5 text-primary-600" />
          {t('vault.compliance.security.title', 'Sécurité Technique')}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <SecurityFeature
            icon={<Key className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.security.encryption.title', 'Chiffrement de bout en bout')}
            description={t(
              'vault.compliance.security.encryption.description',
              "Les coffres salariés concernés chiffrent les documents avant leur envoi."
            )}
            details={[
              t('vault.compliance.security.encryption.d1', 'AES-256-GCM pour les coffres concernés'),
              t('vault.compliance.security.encryption.d2', 'Clés dérivées via PBKDF2-SHA256'),
              t('vault.compliance.security.encryption.d3', '600 000 itérations (recommandation OWASP 2023)'),
              t('vault.compliance.security.encryption.d4', 'Clé unique par document'),
            ]}
          />

          <SecurityFeature
            icon={<EyeOff className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.security.zeroKnowledge.title', 'Architecture Zero-Knowledge')}
            description={t(
              'vault.compliance.security.zeroKnowledge.description',
              "Le contenu est chiffré côté client pour les coffres zero-knowledge."
            )}
            details={[
              t('vault.compliance.security.zeroKnowledge.d1', 'Chiffrement côté client (navigateur)'),
              t('vault.compliance.security.zeroKnowledge.d2', 'Mot de passe jamais transmis en clair'),
              t('vault.compliance.security.zeroKnowledge.d3', 'Déchiffrement uniquement côté client'),
              t('vault.compliance.security.zeroKnowledge.d4', 'Dépôts automatiques via chiffrement hybride RSA'),
            ]}
          />

          <SecurityFeature
            icon={<History className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.security.audit.title', "Piste d'audit immuable")}
            description={t(
              'vault.compliance.security.audit.description',
              "Les entrées enregistrées sont reliées par un chaînage cryptographique vérifiable."
            )}
            details={[
              t('vault.compliance.security.audit.d1', 'Chaînage cryptographique des entrées'),
              t('vault.compliance.security.audit.d2', 'Hash SHA-256 de chaque entrée'),
              t('vault.compliance.security.audit.d3', "Vérification d'intégrité disponible"),
              t('vault.compliance.security.audit.d4', 'Export pour audit légal'),
            ]}
          />

          <SecurityFeature
            icon={<Archive className="h-5 w-5 text-primary-600" />}
            title={t('vault.compliance.security.storage.title', 'Stockage sécurisé')}
            description={t(
              'vault.compliance.security.storage.description',
              "Les garanties d'exploitation doivent être confirmées par les contrats et les mesures de production."
            )}
            details={[
              t('vault.compliance.security.storage.d1', 'Stockage objet configuré par environnement'),
              t('vault.compliance.security.storage.d2', 'Sauvegardes à confirmer en production'),
              t('vault.compliance.security.storage.d3', 'Localisation contractuelle à confirmer'),
              t('vault.compliance.security.storage.d4', 'Aucun SLA public non prouvé'),
            ]}
          />
        </div>
      </section>

      {/* Vos droits */}
      <section className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <Shield className="h-5 w-5 text-primary-600" />
          {t('vault.compliance.rights.title', 'Vos Droits')}
        </h2>
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-500" />
              <div>
                <p className="font-medium text-neutral-900">
                  {t('vault.compliance.rights.access', "Droit d'accès")}
                </p>
                <p className="text-sm text-neutral-600">
                  {t(
                    'vault.compliance.rights.accessDesc',
                    'Accédez à vos documents lorsque le service est disponible'
                  )}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-500" />
              <div>
                <p className="font-medium text-neutral-900">
                  {t('vault.compliance.rights.portability', 'Droit à la portabilité')}
                </p>
                <p className="text-sm text-neutral-600">
                  {t('vault.compliance.rights.portabilityDesc', 'Exportez tous vos documents à tout moment')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-500" />
              <div>
                <p className="font-medium text-neutral-900">
                  {t('vault.compliance.rights.information', "Droit à l'information")}
                </p>
                <p className="text-sm text-neutral-600">
                  {t('vault.compliance.rights.informationDesc', 'Consultez cette documentation à tout moment')}
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-success-500" />
              <div>
                <p className="font-medium text-neutral-900">
                  {t('vault.compliance.rights.audit', "Droit de consultation de l'audit")}
                </p>
                <p className="text-sm text-neutral-600">
                  {t('vault.compliance.rights.auditDesc', 'Visualisez toutes les actions sur votre coffre')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Liens utiles */}
      <section className="mb-8">
        <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-neutral-900">
          <ExternalLink className="h-5 w-5 text-primary-600" />
          {t('vault.compliance.links.title', 'Références Légales')}
        </h2>
        <div className="rounded-xl border border-neutral-200 bg-white p-6">
          <ul className="space-y-3">
            <li>
              <a
                href="https://www.legifrance.gouv.fr/loda/id/JORFTEXT000033558528"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {t('vault.compliance.links.decree', 'Décret n° 2016-1762 du 16 décembre 2016')}
              </a>
            </li>
            <li>
              <a
                href="https://www.legifrance.gouv.fr/codes/article_lc/LEGIARTI000033024022"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {t('vault.compliance.links.laborCode', 'Article L3243-2 du Code du travail')}
              </a>
            </li>
            <li>
              <a
                href="https://www.cnil.fr/fr/reglement-europeen-protection-donnees"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {t('vault.compliance.links.gdpr', 'RGPD - Règlement Général sur la Protection des Données')}
              </a>
            </li>
            <li>
              <a
                href="https://www.ssi.gouv.fr/guide/recommandations-de-securite-relatives-aux-mots-de-passe/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
              >
                <ExternalLink className="h-4 w-4" />
                {t('vault.compliance.links.anssi', "Recommandations ANSSI sur la sécurité des mots de passe")}
              </a>
            </li>
          </ul>
        </div>
      </section>

      {/* Contact */}
      <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-6 text-center">
        <p className="text-sm text-neutral-600">
          {t(
            'vault.compliance.contact',
            "Pour toute question sur la protection de vos données, utilisez le contact données personnelles : rose.yemeli@yso-conseils.com. Cette adresse n'est pas présentée comme celle d'un DPO désigné."
          )}
        </p>
      </div>
    </>
  );
}
